/**
 * Internationalization (i18n) Configuration
 *
 * This module provides type-safe translations for the XER Dashboard.
 *
 * Languages supported: English (default), Spanish, French, German
 *
 * Usage:
 *   import { useTranslation } from '../i18n';
 *   const { t, language, setLanguage } = useTranslation();
 *
 *   // In JSX:
 *   <Button>{t('common.signIn')}</Button>
 */

import { en } from './translations/en';
import { es } from './translations/es';
import { fr } from './translations/fr';
import { de } from './translations/de';
import type { TranslationStructure, TranslationKeys } from './types';

// Re-export types
export type { TranslationStructure, TranslationKeys };

// Language codes
export type Language = 'en' | 'es' | 'fr' | 'de';

// All translations bundled
export const translations: Record<Language, TranslationStructure> = {
  en,
  es,
  fr,
  de,
};

// Language metadata for the switcher
export const languageNames: Record<Language, string> = {
  en: 'English',
  es: 'Español',
  fr: 'Français',
  de: 'Deutsch',
};

// LocalStorage key for persisting language preference
const LANGUAGE_STORAGE_KEY = 'xer-language';

// Get stored language or detect from browser
export function getInitialLanguage(): Language {
  // Check localStorage first
  const stored = localStorage.getItem(LANGUAGE_STORAGE_KEY);
  if (stored && isValidLanguage(stored)) {
    return stored;
  }

  // Detect from browser
  const browserLang = navigator.language.split('-')[0];
  if (isValidLanguage(browserLang)) {
    return browserLang;
  }

  return 'en';
}

// Persist language preference
export function setStoredLanguage(lang: Language): void {
  localStorage.setItem(LANGUAGE_STORAGE_KEY, lang);
}

// Type guard for valid languages
function isValidLanguage(lang: string): lang is Language {
  return ['en', 'es', 'fr', 'de'].includes(lang);
}

// Re-export the hook
export { useTranslation } from './useTranslation';
export { plural } from './plural';
export type { Translate } from './plural';
export { LanguageProvider, useLanguage } from './LanguageContext';
export { LanguageSwitcher } from './LanguageSwitcher';
